import {
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  GridItem,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
} from "@chakra-ui/react"
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import {
  FiAlertTriangle,
  FiArrowRight,
  FiCalendar,
  FiGrid,
  FiInfo,
  FiLayers,
  FiPackage,
  FiSettings,
  FiTool,
} from "react-icons/fi"
import Select from "@/components/Common/AppSelect"
import SearchableSelectModal, {
  type SearchableSelectOption,
} from "@/components/Common/SearchableSelectModal"
import {
  type MachinesResponceDTO,
  MachinesService,
  type ProductionOrderCreateDTO,
  ProductionOrderService,
  type ProductResponceDTO,
  ProductService,
  type TechnologyListResponseDTO,
  type TechnologyResponseDTO,
  TechnologyService,
  WareHouseService,
  type WarehousesDTO,
} from "../../../client"

type Props = {
  onSuccess?: () => void
  onCancel?: () => void
}

export default function AddProductionOrder({ onSuccess, onCancel }: Props) {
  const toast = useToast()
  const qc = useQueryClient()

  const [technologyId, setTechnologyId] = useState("")
  const [machineId, setMachineId] = useState("")
  const [targetQty, setTargetQty] = useState("")
  const [inputWarehouseId, setInputWarehouseId] = useState("")
  const [outputWarehouseId, setOutputWarehouseId] = useState("")
  const [startDate, setStartDate] = useState("")
  const [plannedEndDate, setPlannedEndDate] = useState("")
  const [meldunekNumberPattern, setMeldunekNumberPattern] =
    useState("{PO}-{SEQ}")
  const [isDuplicatePattern, setIsDuplicatePattern] = useState(false)

  useEffect(() => {
    if (!meldunekNumberPattern.trim()) {
      setIsDuplicatePattern(false)
      return
    }
    const handler = setTimeout(() => {
      ProductionOrderService.validatePattern(meldunekNumberPattern)
        .then((res) => setIsDuplicatePattern(res.is_duplicate))
        .catch(() => setIsDuplicatePattern(false))
    }, 500)
    return () => clearTimeout(handler)
  }, [meldunekNumberPattern])

  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const mutedText = useColorModeValue("gray.600", "gray.300")
  const softPanelBg = useColorModeValue("gray.50", "whiteAlpha.100")
  const accentPanelBg = useColorModeValue("blue.50", "blue.900")
  const accentBorder = useColorModeValue("blue.200", "blue.700")
  const iconColor = useColorModeValue("gray.500", "gray.400")

  const { data: technologiesResponse } = useQuery<TechnologyListResponseDTO>({
    queryKey: ["technologies", "list"],
    queryFn: () =>
      TechnologyService.getTechnologyListPage(undefined, "active", 1, 1000),
  })
  const technologies = useMemo(
    () =>
      (technologiesResponse?.items ?? []).filter(
        (technology) => technology.is_active && !technology.is_archived,
      ),
    [technologiesResponse?.items],
  )

  const { data: techDetail } = useQuery<TechnologyResponseDTO>({
    queryKey: ["technology", "detail", technologyId],
    queryFn: () => TechnologyService.getTechnologyByID(technologyId),
    enabled: technologyId.trim() !== "",
  })

  const { data: warehouses } = useQuery<WarehousesDTO[]>({
    queryKey: ["wms", "warehouses", "list"],
    queryFn: () => WareHouseService.getAllWarehouses(),
  })

  const { data: machines } = useQuery<MachinesResponceDTO[]>({
    queryKey: ["machines", "list"],
    queryFn: () => MachinesService.getAllMachines(),
  })

  const technologyOptions = useMemo<SearchableSelectOption[]>(
    () =>
      technologies.map((technology) => ({
        value: technology.id,
        label: `${technology.code} — ${technology.name}`,
        description: technology.description || undefined,
        searchText: [technology.code, technology.name, technology.description]
          .filter(Boolean)
          .join(" "),
      })),
    [technologies],
  )

  const warehouseOptions = useMemo<SearchableSelectOption[]>(
    () =>
      (warehouses ?? []).map((warehouse) => ({
        value: warehouse.id,
        label: `${warehouse.code} — ${warehouse.name}`,
        searchText: [warehouse.code, warehouse.name].filter(Boolean).join(" "),
      })),
    [warehouses],
  )

  const selectedInputWarehouse = useMemo(
    () =>
      (warehouses ?? []).find((warehouse) => warehouse.id === inputWarehouseId),
    [inputWarehouseId, warehouses],
  )
  const selectedOutputWarehouse = useMemo(
    () =>
      (warehouses ?? []).find(
        (warehouse) => warehouse.id === outputWarehouseId,
      ),
    [outputWarehouseId, warehouses],
  )

  const mainOutput = useMemo(() => {
    if (!techDetail || !techDetail.outputs || techDetail.outputs.length === 0)
      return null
    const main =
      techDetail.outputs.find((o) => o.is_main) ?? techDetail.outputs[0]
    return main
  }, [techDetail])

  const { data: mainOutputProduct } = useQuery<ProductResponceDTO | undefined>({
    queryKey: ["products", "detail", mainOutput?.product_id],
    queryFn: () =>
      mainOutput?.product_id
        ? ProductService.getProductByID(mainOutput.product_id)
        : Promise.resolve(undefined as any),
    enabled: !!mainOutput?.product_id,
  })

  const outputProducts = useQueries({
    queries: (techDetail?.outputs ?? []).map((output) => ({
      queryKey: ["products", "detail", output.product_id],
      queryFn: () => ProductService.getProductByID(output.product_id),
      enabled: Boolean(output.product_id),
    })),
  })

  const technologyOutputsSummary = useMemo(
    () =>
      (techDetail?.outputs ?? []).map((output, index) => {
        const product = outputProducts[index]?.data
        return {
          id: output.product_id,
          name: product?.name ?? output.product_id,
          qty: output.qty,
          isMain: output.is_main,
        }
      }),
    [outputProducts, techDetail?.outputs],
  )

  const isTechErr = technologyId.trim() === ""
  const isTargetErr =
    Number.parseFloat(targetQty) <= 0 ||
    Number.isNaN(Number.parseFloat(targetQty))
  const isPatternErr = meldunekNumberPattern.trim() === ""
  const isInputWhErr = inputWarehouseId.trim() === ""
  const isOutputWhErr = outputWarehouseId.trim() === ""
  const selectedTechnologyLabel =
    technologyOptions.find((option) => option.value === technologyId)?.label ??
    null

  const { mutate, isPending } = useMutation({
    mutationKey: ["production-orders", "create"],
    mutationFn: (payload: ProductionOrderCreateDTO) =>
      ProductionOrderService.createProductionOrder(payload),
    onSuccess: async () => {
      toast({ title: "Zlecenie produkcyjne utworzone", status: "success" })
      await qc.invalidateQueries({ queryKey: ["production-orders", "list"] })
      setTechnologyId("")
      setMachineId("")
      setTargetQty("")
      setInputWarehouseId("")
      setOutputWarehouseId("")
      setStartDate("")
      setPlannedEndDate("")
      setMeldunekNumberPattern("{PO}-{SEQ}")
      onSuccess?.()
    },
    onError: (err: any) => {
      toast({
        title: "Błąd tworzenia",
        description: err?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (
      isTechErr ||
      isTargetErr ||
      isInputWhErr ||
      isOutputWhErr ||
      isPatternErr
    )
      return
    const payload: ProductionOrderCreateDTO = {
      technology_id: technologyId,
      machine_id: machineId || undefined,
      target_qty: Number.parseFloat(targetQty),
      input_warehouse_id: inputWarehouseId,
      output_warehouse_id: outputWarehouseId,
      start_date: startDate || undefined,
      planned_end_date: plannedEndDate || undefined,
      meldunek_number_pattern: meldunekNumberPattern.trim(),
    }
    mutate(payload)
  }

  return (
    <VStack as="form" onSubmit={handleSubmit} align="stretch" spacing={4}>
      <Box
        bg={panelBg}
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="2xl"
        p={{ base: 4, md: 5 }}
      >
        <VStack align="stretch" spacing={5}>
          <HStack
            justify="space-between"
            align="start"
            flexWrap="wrap"
            spacing={3}
          >
            <Box>
              <HStack spacing={2} mb={1}>
                <Box color="blue.500">
                  <FiLayers />
                </Box>
                <Text fontSize="lg" fontWeight="semibold">
                  Co produkujemy
                </Text>
              </HStack>
              <Text color={mutedText} fontSize="sm">
                Wybierz technologię, operatora procesu i ilość planowaną dla
                zlecenia.
              </Text>
            </Box>
            <Badge
              colorScheme="blue"
              variant="subtle"
              px={3}
              py={1}
              borderRadius="full"
            >
              Konfiguracja produkcji
            </Badge>
          </HStack>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <GridItem>
              <VStack align="stretch" spacing={4}>
                <FormControl isInvalid={isTechErr} isRequired>
                  <FormLabel>Technologia</FormLabel>
                  <SearchableSelectModal
                    title="Wybierz technologię"
                    value={technologyId}
                    onChange={setTechnologyId}
                    options={technologyOptions}
                    triggerWidth="420px"
                    triggerIcon={<FiSettings />}
                    placeholder="Wybierz technologię"
                    searchPlaceholder="Szukaj po kodzie, nazwie lub opisie"
                    noResultsMessage="Brak technologii pasujących do wyszukiwania"
                    emptyMessage="Brak aktywnych technologii"
                  />
                  <FormErrorMessage>Wybierz technologię</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Maszyna (opcjonalnie)</FormLabel>
                  <Select
                    size="sm"
                    placeholder="Wybierz maszynę"
                    value={machineId}
                    onChange={(e) => setMachineId(e.target.value)}
                    borderRadius="lg"
                    maxW={{ base: "100%", md: "420px" }}
                    leftIcon={<FiTool />}
                  >
                    {(machines ?? []).map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nazwa}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isInvalid={isTargetErr} isRequired>
                  <FormLabel>Ilość docelowa (szt.)</FormLabel>
                  <NumberInput
                    size="sm"
                    value={targetQty}
                    onChange={(valueString) => setTargetQty(valueString)}
                    min={0}
                    step={1}
                    precision={2}
                    clampValueOnBlur={false}
                    maxW={{ base: "100%", md: "320px" }}
                  >
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" color={iconColor}>
                        <FiLayers />
                      </InputLeftElement>
                      <NumberInputField
                        placeholder="np. 1000000"
                        borderRadius="lg"
                        pl={10}
                      />
                    </InputGroup>
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>Podaj ilość &gt; 0</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={isPatternErr} isRequired>
                  <HStack spacing={1} mb={2}>
                    <FormLabel mb={0}>Szablon numeru partii</FormLabel>
                    <Tooltip
                      hasArrow
                      label={
                        <Box p={1}>
                          <Text fontWeight="bold" mb={1}>
                            Dostępne tagi:
                          </Text>
                          <Text>
                            <b>{`{PO}`}</b> - Numer zlecenia produkcyjnego
                          </Text>
                          <Text>
                            <b>{`{SEQ}`}</b> - Numer sekwencyjny
                          </Text>
                          <Text>
                            <b>{`{TECH}`}</b> - Kod technologii
                          </Text>
                          <Text>
                            <b>{`{DD}`}</b>, <b>{`{MM}`}</b>, <b>{`{YY}`}</b>,{" "}
                            <b>{`{YYYY}`}</b> - Data
                          </Text>
                          <Text>
                            <b>{`{RANDOM}`}</b> - Losowy kod (8 znaków)
                          </Text>
                        </Box>
                      }
                      placement="right"
                    >
                      <Box>
                        <Icon as={FiInfo} color="blue.500" />
                      </Box>
                    </Tooltip>
                  </HStack>
                  <Input
                    size="sm"
                    value={meldunekNumberPattern}
                    onChange={(e) => setMeldunekNumberPattern(e.target.value)}
                    placeholder="np. {PO}-{SEQ}"
                    borderRadius="lg"
                    maxW={{ base: "100%", md: "320px" }}
                  />
                  {isDuplicatePattern && (
                    <HStack mt={2} color="orange.500" fontSize="sm">
                      <Icon as={FiAlertTriangle} />
                      <Text>
                        Uwaga: ten szablon jest już używany w innym aktywnym
                        zleceniu.
                      </Text>
                    </HStack>
                  )}
                  <FormErrorMessage>Szablon jest wymagany</FormErrorMessage>
                </FormControl>
              </VStack>
            </GridItem>

            <GridItem>
              <Box
                h="100%"
                bg={softPanelBg}
                borderWidth="1px"
                borderColor={panelBorder}
                borderRadius="xl"
                p={4}
              >
                <VStack align="stretch" spacing={3}>
                  <HStack spacing={2}>
                    <Box color="blue.500">
                      <FiGrid />
                    </Box>
                    <Text fontWeight="semibold">Podsumowanie technologii</Text>
                  </HStack>
                  <Box>
                    <Text fontSize="sm" color={mutedText}>
                      Wybrana technologia
                    </Text>
                    <Text fontWeight="medium">
                      {selectedTechnologyLabel ?? "Jeszcze nie wybrano"}
                    </Text>
                  </Box>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <Box
                      bg={panelBg}
                      borderWidth="1px"
                      borderColor={panelBorder}
                      borderRadius="lg"
                      p={3}
                    >
                      <HStack spacing={2} mb={1}>
                        <Box color={iconColor}>
                          <FiPackage />
                        </Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          color={mutedText}
                        >
                          Produkt główny
                        </Text>
                      </HStack>
                      <Text fontWeight="semibold">
                        {mainOutputProduct?.name ??
                          mainOutput?.product_id ??
                          "—"}
                      </Text>
                    </Box>
                    <Box
                      bg={panelBg}
                      borderWidth="1px"
                      borderColor={panelBorder}
                      borderRadius="lg"
                      p={3}
                    >
                      <HStack spacing={2} mb={1}>
                        <Box color={iconColor}>
                          <FiLayers />
                        </Box>
                        <Text
                          fontSize="xs"
                          textTransform="uppercase"
                          color={mutedText}
                        >
                          Batch / opak.
                        </Text>
                      </HStack>
                      <Text fontWeight="semibold">
                        {mainOutput?.qty ? `${mainOutput.qty}` : "—"}
                      </Text>
                    </Box>
                  </SimpleGrid>
                  <Box>
                    <HStack spacing={2} mb={2}>
                      <Box color={iconColor}>
                        <FiPackage />
                      </Box>
                      <Text fontSize="sm" color={mutedText}>
                        Produkty na wyjściu
                      </Text>
                    </HStack>
                    {technologyOutputsSummary.length > 0 ? (
                      <List spacing={2}>
                        {technologyOutputsSummary.map((output) => (
                          <ListItem
                            key={output.id}
                            bg={panelBg}
                            borderWidth="1px"
                            borderColor={panelBorder}
                            borderRadius="lg"
                            px={3}
                            py={2}
                          >
                            <HStack
                              justify="space-between"
                              align="start"
                              spacing={3}
                            >
                              <Box minW={0}>
                                <Text fontWeight="semibold" noOfLines={1}>
                                  {output.name}
                                </Text>
                                {output.isMain ? (
                                  <Badge
                                    mt={1}
                                    colorScheme="green"
                                    variant="subtle"
                                  >
                                    Główny output
                                  </Badge>
                                ) : null}
                              </Box>
                              <Text whiteSpace="nowrap" color={mutedText}>
                                {output.qty} / opak.
                              </Text>
                            </HStack>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Text>Brak zdefiniowanych outputów.</Text>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedText}>
                      Opis
                    </Text>
                    <Text noOfLines={3}>
                      {techDetail?.description?.trim() ||
                        "Brak opisu dla wybranej technologii."}
                    </Text>
                  </Box>
                </VStack>
              </Box>
            </GridItem>
          </SimpleGrid>
        </VStack>
      </Box>

      <Box
        bg={panelBg}
        borderWidth="1px"
        borderColor={panelBorder}
        borderRadius="2xl"
        p={{ base: 4, md: 5 }}
      >
        <VStack align="stretch" spacing={5}>
          <Box>
            <HStack spacing={2} mb={1}>
              <Box color="blue.500">
                <FiArrowRight />
              </Box>
              <Text fontSize="lg" fontWeight="semibold">
                Skąd i dokąd
              </Text>
            </HStack>
            <Text color={mutedText} fontSize="sm">
              Określ przepływ materiałów i harmonogram realizacji zlecenia.
            </Text>
          </Box>

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <FormControl isInvalid={isInputWhErr} isRequired>
              <FormLabel>Magazyn materiałów</FormLabel>
              <SearchableSelectModal
                title="Wybierz magazyn materiałów"
                value={inputWarehouseId}
                onChange={setInputWarehouseId}
                options={warehouseOptions}
                triggerIcon={<FiPackage />}
                placeholder="Wybierz magazyn"
                searchPlaceholder="Szukaj po kodzie lub nazwie magazynu"
                noResultsMessage="Brak magazynów pasujących do wyszukiwania"
                emptyMessage="Brak dostępnych magazynów"
              />
              <FormErrorMessage>Wybierz magazyn</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={isOutputWhErr} isRequired>
              <FormLabel>Magazyn produkcji</FormLabel>
              <SearchableSelectModal
                title="Wybierz magazyn produkcji"
                value={outputWarehouseId}
                onChange={setOutputWarehouseId}
                options={warehouseOptions}
                triggerIcon={<FiPackage />}
                placeholder="Wybierz magazyn"
                searchPlaceholder="Szukaj po kodzie lub nazwie magazynu"
                noResultsMessage="Brak magazynów pasujących do wyszukiwania"
                emptyMessage="Brak dostępnych magazynów"
              />
              <FormErrorMessage>Wybierz magazyn</FormErrorMessage>
            </FormControl>
          </SimpleGrid>

          <Box
            bg={accentPanelBg}
            borderWidth="1px"
            borderColor={accentBorder}
            borderRadius="xl"
            p={4}
          >
            <SimpleGrid
              columns={{ base: 1, lg: 3 }}
              spacing={4}
              alignItems="center"
            >
              <Box>
                <HStack spacing={2} mb={1}>
                  <Box color={iconColor}>
                    <FiPackage />
                  </Box>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    color={mutedText}
                  >
                    Wejście
                  </Text>
                </HStack>
                <Text fontWeight="semibold">
                  {selectedInputWarehouse
                    ? `${selectedInputWarehouse.code} — ${selectedInputWarehouse.name}`
                    : "Wybierz magazyn materiałów"}
                </Text>
              </Box>
              <Box textAlign="center">
                <HStack justify="center" spacing={2} mb={1}>
                  <Box color={iconColor}>
                    <FiArrowRight />
                  </Box>
                  <Text fontSize="sm" color={mutedText}>
                    Przepływ procesu
                  </Text>
                </HStack>
                <Text fontWeight="bold">materialy -&gt; produkcja</Text>
              </Box>
              <Box textAlign={{ base: "left", lg: "right" }}>
                <HStack
                  spacing={2}
                  justify={{ base: "flex-start", lg: "flex-end" }}
                  mb={1}
                >
                  <Box color={iconColor}>
                    <FiPackage />
                  </Box>
                  <Text
                    fontSize="xs"
                    textTransform="uppercase"
                    color={mutedText}
                  >
                    Wyjście
                  </Text>
                </HStack>
                <Text fontWeight="semibold">
                  {selectedOutputWarehouse
                    ? `${selectedOutputWarehouse.code} — ${selectedOutputWarehouse.name}`
                    : "Wybierz magazyn produkcji"}
                </Text>
              </Box>
            </SimpleGrid>
          </Box>

          <Divider />

          <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={4}>
            <FormControl>
              <FormLabel>Data startu</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color={iconColor}>
                  <FiCalendar />
                </InputLeftElement>
                <Input
                  size="sm"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  borderRadius="lg"
                  pl={10}
                />
              </InputGroup>
            </FormControl>
            <FormControl>
              <FormLabel>Planowany koniec</FormLabel>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color={iconColor}>
                  <FiCalendar />
                </InputLeftElement>
                <Input
                  size="sm"
                  type="date"
                  value={plannedEndDate}
                  onChange={(e) => setPlannedEndDate(e.target.value)}
                  borderRadius="lg"
                  pl={10}
                />
              </InputGroup>
            </FormControl>
          </SimpleGrid>
        </VStack>
      </Box>

      <HStack justify="flex-end">
        <Button size="sm" variant="ghost" onClick={onCancel} type="button">
          Anuluj
        </Button>
        <Button
          size="sm"
          colorScheme="blue"
          type="submit"
          isLoading={isPending}
          isDisabled={
            isTechErr ||
            isTargetErr ||
            isInputWhErr ||
            isOutputWhErr ||
            isPatternErr
          }
        >
          Utwórz
        </Button>
      </HStack>
    </VStack>
  )
}
